import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Mail, ArrowRight, BookOpen, Zap, TrendingUp, Star, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { publicApi } from '../utils/api';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1520637836862-4d197d17c55a?auto=format&fit=crop&w=1200&q=80';

const FALLBACK_ISSUES = [
  {
    id: 1, number: '#047', date: 'Apr 1, 2026', tag: 'Agent Week',
    title: 'The AI Agent Tipping Point — 5 Workflows You Can Deploy Today',
    summary: 'AI agents crossed a critical threshold this week. We analysed 14 operator deployments and found the 5 workflow patterns that consistently deliver ROI in under 30 days. Plus: CrewAI vs LangGraph head-to-head, and why n8n just became the automation default.',
    readTime: '6 min', highlight: true,
    sections: [
      { icon: '?', label: 'Spotlight', text: 'n8n v1.40 ships native LLM agent nodes — build full autonomous pipelines without code.' },
      { icon: '??', label: 'Field Test', text: 'We ran CrewAI on a 10-step lead enrichment workflow. Verdict: production-ready if you control the LLM calls.' },
      { icon: '??', label: 'Pricing Alert', text: 'OpenAI raised GPT-4o API pricing 15%. Now\'s the time to audit your token usage.' },
      { icon: '??', label: 'Stack Tip', text: 'Pair Relevance AI + Clay for account research. Cuts research time from 2hr to 8 min per account.' },
    ],
    tools: ['n8n', 'CrewAI', 'Relevance AI', 'Clay'],
  },
  {
    id: 2, number: '#046', date: 'Mar 25, 2026', tag: 'Writing AI',
    title: 'Claude 3.7 vs GPT-4o for Content Teams — The Real Verdict After 30 Days',
    summary: 'We put both models through a rigorous 30-day content production test across 6 content types. The winner isn\'t who you expect, and it depends entirely on your workflow. Full breakdown + our recommended stack configuration.',
    readTime: '8 min', highlight: false,
    sections: [
      { icon: '??', label: 'Winner', text: 'Claude 3.7 wins on long-form brand content (3000+ words). GPT-4o wins on structured SEO output.' },
      { icon: '??', label: 'Data', text: '83% of content managers in our survey now use 2+ models. Single-model workflows are fading.' },
      { icon: '??', label: 'New Tool', text: 'Notion AI gets context-aware tone matching. Finally useful for editorial teams.' },
      { icon: '??', label: 'Integration', text: 'Hook Claude into your CMS via n8n — template inside the issue.' },
    ],
    tools: ['Claude Pro', 'ChatGPT Plus', 'Notion AI', 'Jasper'],
  },
  {
    id: 3, number: '#045', date: 'Mar 18, 2026', tag: 'Image AI',
    title: 'Midjourney v6.1 Deep Dive: When Stock Photos Die',
    summary: 'With v6.1\'s photorealism, we calculated exactly when it becomes cheaper to generate than to license stock. Spoiler: it already has for most teams. Full ROI calculator inside, plus our prompt framework for brand-consistent outputs.',
    readTime: '5 min', highlight: false,
    sections: [
      { icon: '??', label: 'Key Finding', text: 'Teams using 50+ stock images/month save an average of $380/mo by switching to Midjourney.' },
      { icon: '??', label: 'New Feature', text: 'Style reference weight control lets you lock brand aesthetics across all generations.' },
      { icon: '??', label: 'Watch Out', text: 'Commercial IP issues still unresolved for character likeness. Consult legal before people-heavy assets.' },
      { icon: '??', label: 'Prompt Framework', text: 'Our 6-part prompt structure for commercial-grade outputs — in the full issue.' },
    ],
    tools: ['Midjourney v6', 'Adobe Firefly', 'DALL-E 3'],
  },
  {
    id: 4, number: '#044', date: 'Mar 11, 2026', tag: 'ROI Report',
    title: 'Q1 2026 AI Stack ROI Report: What Actually Worked',
    summary: '114 operators, 6 months of data. We mapped which AI tools delivered measurable ROI and which promised more than they delivered. The results will reshape your renewal decisions for Q2.',
    readTime: '10 min', highlight: false,
    sections: [
      { icon: '??', label: 'Top ROI Tools', text: 'n8n (automation), Claude Pro (content), Akkio (analytics) led the ROI rankings for SMB teams.' },
      { icon: '?', label: 'Disappointing', text: '41% of enterprise "AI platforms" showed no measurable productivity change. Vendor consolidation is coming.' },
      { icon: '??', label: 'Insight', text: 'Teams with a designated AI ops owner saw 2.4x better ROI than teams with no owner.' },
      { icon: '???', label: 'Framework', text: 'Download our Q2 2026 stack audit template — links in the full issue.' },
    ],
    tools: ['n8n', 'Claude Pro', 'Akkio', 'Make'],
  },
];

const extractPlainText = (html) => String(html || '')
  .replace(/<style[\s\S]*?<\/style>/gi, ' ')
  .replace(/<script[\s\S]*?<\/script>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const extractFirstImage = (html) => {
  const m = String(html || '').match(/<img[^>]+src=["']([^"']+)["'][^>]*>/i);
  return m?.[1] || '';
};

const parseWebIssueContent = (htmlString) => {
  const fallback = {
    heroImage: extractFirstImage(htmlString),
    intro: '',
    sections: [],
    bullets: [],
    links: [],
  };
  if (!htmlString || typeof window === 'undefined' || !window.DOMParser) {
    return fallback;
  }

  try {
    const doc = new window.DOMParser().parseFromString(String(htmlString || ''), 'text/html');
    doc.querySelectorAll('script, style, noscript, meta, title').forEach((n) => n.remove());

    const textOf = (node) => (node?.textContent || '').replace(/\s+/g, ' ').trim();
    const firstImg = doc.querySelector('img[src]');
    const heroImage = firstImg?.getAttribute('src') || fallback.heroImage;

    const intro = Array.from(doc.querySelectorAll('p'))
      .map(textOf)
      .find((line) => line.length >= 40) || '';

    const headings = Array.from(doc.querySelectorAll('h2, h3, h4'))
      .map(textOf)
      .filter(Boolean)
      .slice(0, 4);

    const paragraphs = Array.from(doc.querySelectorAll('p'))
      .map(textOf)
      .filter((line) => line.length >= 24)
      .slice(0, 8);

    const sections = headings.map((h, i) => ({
      label: h,
      text: paragraphs[i] || paragraphs[i + 1] || '',
    })).filter((s) => s.text);

    const bullets = Array.from(doc.querySelectorAll('li'))
      .map(textOf)
      .filter((line) => line.length >= 8)
      .slice(0, 8);

    const links = Array.from(doc.querySelectorAll('a[href]'))
      .map((a) => ({
        label: textOf(a) || 'Open link',
        url: a.getAttribute('href') || '#',
      }))
      .filter((item) => item.url && !item.url.startsWith('mailto:'))
      .slice(0, 4);

    return {
      heroImage,
      intro,
      sections,
      bullets,
      links,
    };
  } catch {
    return fallback;
  }
};

// ── Teaser card — shows when NOT logged in ────────────────────────────────
const TeaserCard = ({ issue, expanded, onToggle }) => {
  const ref = useRef(null);
  const preview = issue.bodyPreview || issue.summary || '';

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: 'var(--bg-elevated)', border: issue.highlight ? '1px solid rgba(0,255,135,0.35)' : '1px solid var(--border-subtle)' }}>
      {/* Header row */}
      <div className="p-5 pb-3">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{issue.number}</span>
          <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{issue.date}</span>
          <span className="text-xs px-2 py-0.5 rounded-md font-medium"
            style={{ background: issue.highlight ? 'rgba(0,255,135,0.12)' : 'var(--bg-base)', color: issue.highlight ? '#00ff87' : 'var(--text-muted)' }}>
            {issue.tag}
          </span>
          {issue.highlight && <span className="text-xs px-2 py-0.5 rounded-md font-bold" style={{ background: 'rgba(255,232,0,0.15)', color: '#FFE400' }}>Latest</span>}
        </div>
        <h3 className="font-bold text-base leading-snug mb-2" style={{ color: 'var(--text-primary)' }}>{issue.title}</h3>
        <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{issue.summary}</p>

        {/* Gradient-faded teaser — always 5 lines, fade out */}
        {!expanded && preview && (
          <div className="relative" style={{ maxHeight: '6.5rem', overflow: 'hidden' }}>
            <p ref={ref} className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{preview}</p>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2.5rem',
              background: 'linear-gradient(transparent, var(--bg-elevated))' }} />
          </div>
        )}

        {/* Full body text when expanded */}
        {expanded && preview && (
          <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>{preview}</p>
        )}

        {/* Expanded sections */}
        {expanded && Array.isArray(issue.sections) && issue.sections.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 mb-3">
            {issue.sections.map((s, i) => (
              <div key={i} className="p-3 rounded-xl" style={{ background: 'var(--bg-base)' }}>
                <span className="text-xs font-bold block mb-1" style={{ color: 'var(--accent-primary)' }}>{s.label}</span>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tools & toggle row */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {(issue.tools || []).map((t) => (
            <span key={t} className="text-xs px-2 py-0.5 rounded-md"
              style={{ background: 'var(--bg-base)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}>{t}</span>
          ))}
          <button type="button" onClick={onToggle}
            className="ml-auto flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium transition-opacity hover:opacity-80"
            style={{ background: 'var(--bg-base)', color: 'var(--accent-primary)', border: '1px solid rgba(0,255,135,0.25)' }}>
            {expanded ? 'Show less' : 'More...'}
            <ChevronDown size={13} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
        </div>
        <div className="mt-2">
          <Link to="/newsletter" className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Subscribe for full issues — delivered every Monday <ArrowRight size={11} className="inline" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ── Full-mode card — for logged-in clients ────────────────────────────────
const FullCard = ({ issue }) => {
  const webContent = parseWebIssueContent(issue.renderedHtml || '');
  const hasSections = webContent.sections.length > 0 || webContent.bullets.length > 0 || webContent.links.length > 0;
  const heroImg = webContent.heroImage || issue.heroImage || HERO_IMAGE;
  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: 'var(--bg-elevated)', border: issue.highlight ? '1px solid rgba(0,255,135,0.35)' : '1px solid var(--border-subtle)' }}>
      {/* Issue header */}
      <div className="px-6 pt-5 pb-4" style={{ background: 'var(--bg-elevated)' }}>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{issue.number}</span>
          <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{issue.date}</span>
          <span className="text-xs px-2 py-0.5 rounded-md font-medium"
            style={{ background: issue.highlight ? 'rgba(0,255,135,0.12)' : 'var(--bg-base)', color: issue.highlight ? '#00ff87' : 'var(--text-muted)' }}>
            {issue.tag}
          </span>
          {issue.highlight && <span className="text-xs px-2 py-0.5 rounded-md font-bold" style={{ background: 'rgba(255,232,0,0.15)', color: '#FFE400' }}>Latest</span>}
          <span className="text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>{issue.readTime} read</span>
        </div>
        <h3 className="font-bold text-xl leading-snug mb-1" style={{ color: 'var(--text-primary)' }}>{issue.title}</h3>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{issue.summary}</p>
      </div>

      {/* Web-friendly transformed content (not raw email embed) */}
      {heroImg && (
        <div className="w-full overflow-hidden" style={{ maxHeight: 300 }}>
          <img src={heroImg} alt={issue.title} className="w-full object-cover" style={{ maxHeight: 300 }} loading="lazy" />
        </div>
      )}

      <div className="px-6 py-5">
        {(webContent.intro || issue.bodyPreview) && (
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            {webContent.intro || issue.bodyPreview}
          </p>
        )}

        {webContent.sections.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {webContent.sections.map((section, idx) => (
              <div key={`${issue.id}-section-${idx}`} className="p-3 rounded-xl" style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle)' }}>
                <span className="text-xs font-bold block mb-1" style={{ color: 'var(--accent-primary)' }}>{section.label}</span>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{section.text}</span>
              </div>
            ))}
          </div>
        )}

        {webContent.bullets.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Key Points</p>
            <ul className="space-y-1.5">
              {webContent.bullets.map((point, idx) => (
                <li key={`${issue.id}-point-${idx}`} className="text-sm" style={{ color: 'var(--text-secondary)' }}>• {point}</li>
              ))}
            </ul>
          </div>
        )}

        {webContent.links.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            {webContent.links.map((link, idx) => (
              <a
                key={`${issue.id}-link-${idx}`}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-2.5 py-1 rounded-lg transition-opacity hover:opacity-80"
                style={{ background: 'rgba(0,255,135,0.08)', color: '#00ff87', border: '1px solid rgba(0,255,135,0.25)' }}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}

        {!hasSections && issue.bodyPreview && (
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{issue.bodyPreview}</p>
        )}
      </div>
    </div>
  );
};

// ── Classic four newsletter cards (always full view, no iframe) ───────────
const ClassicCard = ({ issue }) => (
  <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
    {/* Dark header band */}
    <div className="px-6 pt-5 pb-4" style={{ background: '#0f172a' }}>
      <p className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
        Wowrolla Weekly · {issue.date}
      </p>
      <h3 className="text-xl font-bold text-white leading-snug mb-1">{issue.title}</h3>
      <div className="flex gap-2 mt-2">
        <span className="text-xs px-2 py-0.5 rounded-md font-medium" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>{issue.tag}</span>
        <span className="text-xs px-2 py-0.5 rounded-md" style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.5)' }}>{issue.number}</span>
        <span className="text-xs px-2 py-0.5 rounded-md" style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.5)' }}>{issue.readTime} read</span>
      </div>
    </div>

    {/* Hero image */}
    <div className="w-full overflow-hidden" style={{ maxHeight: 260 }}>
      <img src={HERO_IMAGE} alt={issue.title} className="w-full object-cover" style={{ maxHeight: 260 }} loading="lazy" />
    </div>

    {/* Summary */}
    <div className="px-6 pt-5 pb-3">
      <p className="text-base leading-relaxed mb-4" style={{ color: 'var(--text-primary)' }}>{issue.summary}</p>

      {/* Section cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {(issue.sections || []).map((s, i) => (
          <div key={i} className="p-4 rounded-xl" style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle)' }}>
            <span className="text-sm font-bold block mb-1" style={{ color: 'var(--accent-primary)' }}>{s.label}</span>
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{s.text}</span>
          </div>
        ))}
      </div>

      {/* Tools */}
      <div className="flex flex-wrap items-center gap-2 pt-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Tools covered:</span>
        {(issue.tools || []).map((t) => (
          <Link key={t} to={`/search?q=${encodeURIComponent(t)}`}
            className="text-xs px-2.5 py-1 rounded-lg transition-opacity hover:opacity-80"
            style={{ background: 'rgba(0,255,135,0.08)', color: '#00ff87', border: '1px solid rgba(0,255,135,0.25)' }}>
            {t}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const NewsletterPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [publishedIssues, setPublishedIssues] = useState([]);
  const [showAllLive, setShowAllLive] = useState(false);

  useEffect(() => {
    let mounted = true;
    publicApi.getNewsletterIssues(12).then((res) => {
      if (mounted) setPublishedIssues(Array.isArray(res?.data?.items) ? res.data.items : []);
    }).catch(() => { if (mounted) setPublishedIssues([]); });
    return () => { mounted = false; };
  }, []);

  const isClientLoggedIn = useMemo(() => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return false;
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      return String(u?.role || '').toLowerCase() === 'user';
    } catch { return false; }
  }, []);

  const liveIssues = useMemo(() => publishedIssues.map((item, index) => {
    const publishedAt = item.published_at ? new Date(item.published_at) : null;
    const date = (publishedAt && !Number.isNaN(publishedAt.getTime()))
      ? publishedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'Recent';
    const rendered = String(item.rendered_html || '');
    const plain = extractPlainText(rendered);
    return {
      id: item.id || `issue-${index}`,
      number: `#${String(index + 1).padStart(3, '0')}`,
      date,
      tag: 'Published',
      title: item.title || item.subject || 'Wowrolla Newsletter',
      summary: String(item.preview_text || '').trim() || 'Published newsletter issue.',
      bodyPreview: plain.length > 600 ? `${plain.slice(0, 597)}...` : plain,
      heroImage: extractFirstImage(rendered) || HERO_IMAGE,
      renderedHtml: rendered,
      readTime: `${Math.max(3, Math.ceil(plain.length / 900))} min`,
      highlight: index === 0,
      sections: [],
      tools: ['Newsletter'],
      ctaUrl: item.cta_url || '/newsletter',
    };
  }), [publishedIssues]);

  const visibleLive = useMemo(() =>
    (isClientLoggedIn || showAllLive) ? liveIssues : liveIssues.slice(0, 1),
    [isClientLoggedIn, showAllLive, liveIssues]);

  const statusMessage = useMemo(() => {
    const s = new URLSearchParams(location.search).get('status');
    if (s === 'confirmed') return 'Subscription confirmed. Your first digest arrives Monday at 9:00 AM.';
    if (s === 'invalid') return 'Confirmation link is invalid or expired. Subscribe again.';
    return '';
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    try {
      const res = await publicApi.newsletterSubscribe({ email, source_page: '/newsletter', role: null });
      setMessage(res.data?.message || 'Check your inbox to confirm your subscription.');
      setEmail('');
    } catch (err) {
      setMessage(err?.response?.data?.detail || 'Could not submit right now. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-devices" data-testid="newsletter-page">
      <Helmet>
        <title>Wowrolla Weekly — AI Stack Intelligence</title>
        <meta name="description" content="Weekly AI tool intelligence for operators: new tools, pricing shifts, ROI data, and practical stack tips." />
      </Helmet>

      <section
        className="relative overflow-hidden resource-hero"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.68), rgba(247,252,255,0.82)), url(${IMG.hero.newsletter})`
            : `linear-gradient(rgba(8,8,18,0.72), rgba(8,8,18,0.94)), url(${IMG.hero.newsletter})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 pb-12 relative z-10">
          <Link to="/" className="inline-flex items-center mb-8 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft size={15} className="mr-2" /> Back to Home
          </Link>

          {/* Hero grid */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mb-16">
          <div className="lg:col-span-3">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
              <p className="text-xs font-mono uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--accent-primary)' }}>Every Monday · Free</p>
              <h1 className="text-4xl sm:text-5xl font-black mb-4 text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                AI Stack Intelligence<br />
                <span style={{ color: 'var(--accent-primary)' }}>for Operators.</span>
              </h1>
              <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
                Every Monday: new AI tools worth your attention, pricing shifts, ROI data from real deployments, one deep-dive, and one actionable stack tip.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {[
                  { icon: <Zap size={18} />, label: '3 New Tools', sub: 'each week' },
                  { icon: <TrendingUp size={18} />, label: 'ROI Data', sub: 'from operators' },
                  { icon: <Star size={18} />, label: 'Deep Dives', sub: 'one per issue' },
                  { icon: <BookOpen size={18} />, label: 'Stack Tips', sub: 'actionable' },
                ].map((item) => (
                  <div key={item.label} className="p-3 rounded-xl text-center" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                    <div className="flex justify-center mb-2" style={{ color: 'var(--accent-primary)' }}>{item.icon}</div>
                    <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{item.label}</div>
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.sub}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
            className="lg:col-span-2 p-6 rounded-2xl self-start"
            style={{ background: 'var(--bg-elevated)', border: '1px solid rgba(0,255,135,0.25)', boxShadow: '0 0 30px rgba(0,255,135,0.06)' }}>
            <Mail size={28} className="mb-4" style={{ color: 'var(--accent-primary)' }} />
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Join 4,200+ operators</h2>
            <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>Free. Unsubscribe any time. No spam, ever.</p>
            {statusMessage && (
              <div className="p-3 mb-4 rounded-lg text-sm" style={{ background: 'rgba(0,255,135,0.08)', border: '1px solid rgba(0,255,135,0.25)', color: '#00ff87' }}>
                {statusMessage}
              </div>
            )}
            <form onSubmit={submit} className="space-y-3" data-testid="newsletter-page-form">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="input-pro w-full" placeholder="you@company.com" />
              <button type="submit" disabled={submitting} className="btn-primary-enhanced w-full">
                {submitting ? 'Submitting...' : 'Get the Weekly Digest →'}
              </button>
            </form>
            <p className="text-xs mt-3" style={{ color: 'var(--text-muted)' }}>Double opt-in (GDPR). Confirm by email before being added.</p>
            {message && <p className="text-sm mt-3" style={{ color: 'var(--accent-primary)' }}>{message}</p>}
          </motion.div>
        </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-12">

        {/* ── PAST ISSUES ──────────────────────────────────────────── */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Past Issues</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
            {isClientLoggedIn ? 'Full newsletter view — you are logged in.' : 'Subscribe or log in for the complete issue. Teaser shown below.'}
          </p>
        </div>

        <div className="space-y-6">
          {(visibleLive.length > 0 ? visibleLive : FALLBACK_ISSUES.slice(0, 1)).map((issue, idx) => (
            <motion.div key={issue.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.07 }}>
              {isClientLoggedIn
                ? <FullCard issue={issue} />
                : <TeaserCard issue={issue} expanded={expanded === issue.id} onToggle={() => setExpanded(expanded === issue.id ? null : issue.id)} />}
            </motion.div>
          ))}
        </div>

        {!isClientLoggedIn && liveIssues.length > 1 && !showAllLive && (
          <div className="mt-4 text-center">
            <button type="button" className="btn-secondary" onClick={() => setShowAllLive(true)}>
              Show More Issues
            </button>
          </div>
        )}

        {/* ── CLASSIC FOUR ─────────────────────────────────────────── */}
        <div className="mt-12 mb-6">
          <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Classic Issues</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
            Sample editions shown in full newsletter layout with images, sections, and tool recommendations.
          </p>
        </div>

        <div className="space-y-8">
          {FALLBACK_ISSUES.map((issue, idx) => (
            <motion.div key={`classic-${issue.id}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 * idx }}>
              <ClassicCard issue={issue} />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center p-8 rounded-2xl"
          style={{ background: 'linear-gradient(135deg, rgba(0,255,135,0.06), rgba(255,232,0,0.04))', border: '1px solid rgba(0,255,135,0.2)' }}>
          <h3 className="font-bold text-xl mb-2 text-white">Get the next issue in your inbox</h3>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Every Monday. Free. Unsubscribe any time.</p>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="input-pro flex-1" placeholder="you@company.com" />
            <button type="submit" disabled={submitting} className="btn-primary-enhanced flex-shrink-0">
              {submitting ? '...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default NewsletterPage;
